import { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { ArrowUp, ArrowDown, Minus, ArrowsLeftRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, ChartTip, useMoney, periodLabel } from "./shared";
import PeriodPicker from "./PeriodPicker";

// invert: menor es mejor. neutral: sin juicio.
const GROUPS = [
  { name: "Ventas", items: [
    { key: "sales_total_mxn", label: "Venta total", type: "money" },
    { key: "products_sales_mxn", label: "Venta de productos", type: "money" },
    { key: "repair_sales_mxn", label: "Venta de reparaciones", type: "money" },
    { key: "notes_count", label: "Notas de venta", type: "int" },
    { key: "units_sold", label: "Piezas vendidas", type: "int" },
    { key: "avg_ticket_mxn", label: "Ticket promedio", type: "money" },
    { key: "avg_daily_mxn", label: "Venta promedio diaria", type: "money" },
  ] },
  { name: "Clientes", items: [
    { key: "clients_count", label: "Clientes que compraron", type: "int" },
    { key: "new_clients_count", label: "Clientes nuevos", type: "int" },
    { key: "recurrent_clients_count", label: "Recurrentes en el mes", type: "int" },
    { key: "returned_clients_count", label: "Clientes que regresaron", type: "int" },
    { key: "retention_pct", label: "% de retención", type: "pct" },
  ] },
  { name: "Rentabilidad", items: [
    { key: "gross_profit_mxn", label: "Utilidad bruta", type: "money" },
    { key: "expenses_total_mxn", label: "Gastos operativos", type: "money", invert: true },
    { key: "net_profit_mxn", label: "Utilidad neta", type: "money" },
    { key: "net_margin_pct", label: "Margen neto", type: "pct" },
    { key: "pending_mxn", label: "Pendiente por cobrar", type: "money", invert: true },
    { key: "no_cost_notes", label: "Notas sin costo registrado", type: "int", invert: true },
  ] },
  { name: "Cobranza", items: [
    { key: "received_total_mxn", label: "Dinero recibido (por fecha de pago)", type: "money" },
    { key: "from_other_months_mxn", label: "Cobrado de meses anteriores", type: "money", neutral: true },
  ] },
  { name: "Reparaciones", items: [
    { key: "tickets_created", label: "Tickets creados", type: "int", neutral: true },
    { key: "repairs_created", label: "Notas de reparación", type: "int", neutral: true },
    { key: "repairs_paid", label: "Reparaciones cobradas", type: "int" },
  ] },
];

function Delta({ a, b, type, invert, neutral, fmtV }) {
  const diff = Number(b || 0) - Number(a || 0);
  if (Math.abs(diff) < 0.005) return <span className="inline-flex items-center gap-1 text-zinc-400 text-xs"><Minus size={11} /> igual</span>;
  const pct = a ? (diff / Math.abs(a)) * 100 : null;
  const up = diff > 0;
  const good = neutral ? null : invert ? !up : up;
  const color = good === null ? "text-zinc-600" : good ? "text-emerald-700" : "text-[#E0000F]";
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-1 numeric text-xs font-semibold ${color}`}>
      <Icon size={11} weight="bold" />
      {type === "pct" ? `${diff > 0 ? "+" : ""}${diff.toFixed(1)} pts` : `${diff > 0 ? "+" : "-"}${fmtV(Math.abs(diff))}`}
      {pct !== null && type !== "pct" && <span className="text-[10px] font-normal opacity-80">({pct > 0 ? "+" : ""}{pct.toFixed(0)}%)</span>}
    </span>
  );
}

export default function CompareView({ a, b, onChange, periods }) {
  const { fmt, compact } = useMoney();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!a || !b) return;
    setLoading(true);
    api.get("/reports/monthly/compare", { params: { a, b } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo comparar los periodos"))
      .finally(() => setLoading(false));
  }, [a, b]);

  const fmtV = (type) => (v) => (type === "money" ? fmt(v) : type === "pct" ? `${Number(v || 0).toFixed(1)}%` : Number(v || 0).toLocaleString("en-US"));

  const cumulative = useMemo(() => {
    if (!data) return [];
    const n = Math.max(data.daily_a.length, data.daily_b.length);
    let ca = 0, cb = 0;
    return Array.from({ length: n }, (_, i) => {
      const da = data.daily_a[i], db = data.daily_b[i];
      if (da) ca += da.total_mxn;
      if (db) cb += db.total_mxn;
      return { day: i + 1, a: da ? ca : null, b: db ? cb : null };
    });
  }, [data]);

  const swap = () => onChange(b, a);

  return (
    <div className="space-y-5" data-testid="mr-compare">
      <Card testid="mr-compare-controls">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <div className="text-[10px] font-mono-data uppercase tracking-widest text-zinc-500 mb-1">Periodo A (base)</div>
            <PeriodPicker value={a} onChange={(v) => onChange(v, b)} periods={periods} testid="mr-cmp-a" compact />
          </div>
          <button type="button" onClick={swap} className="h-9 px-3 border border-zinc-200 bg-white inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50" data-testid="mr-cmp-swap" title="Intercambiar"><ArrowsLeftRight size={14} /> Intercambiar</button>
          <div>
            <div className="text-[10px] font-mono-data uppercase tracking-widest text-zinc-500 mb-1">Periodo B (comparado)</div>
            <PeriodPicker value={b} onChange={(v) => onChange(a, v)} periods={periods} testid="mr-cmp-b" compact />
          </div>
          <div className="text-xs text-zinc-500 max-w-md">La diferencia se lee como <b>B respecto a A</b>. Verde = mejoró, rojo = empeoró. El inventario no se compara porque es una fotografía actual.</div>
        </div>
        {data && (data.a.is_current_month || data.b.is_current_month) && (
          <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-3 py-1.5 inline-block" data-testid="mr-cmp-partial">
            {data.a.is_current_month ? `${periodLabel(a)} lleva ${data.a.elapsed_days} día(s)` : `${periodLabel(b)} lleva ${data.b.elapsed_days} día(s)`}: mes en curso, la comparación es parcial.
          </div>
        )}
      </Card>

      {loading && !data && <div className="text-sm text-zinc-500 py-10 text-center">Comparando…</div>}

      {data && (
        <>
          <Card title="Venta acumulada por día del mes" desc="Cómo avanzó cada mes día a día (acumulado)." testid="mr-cmp-chart">
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cumulative} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#71717a" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#71717a" }} axisLine={false} tickLine={false} width={58} tickFormatter={(v) => compact(v)} />
                  <Tooltip content={<ChartTip fmt={(v) => fmt(v)} />} labelFormatter={(l) => `Día ${l}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="a" name={periodLabel(a)} stroke="#71717a" strokeWidth={2} dot={false} connectNulls={false} strokeDasharray="4 3" />
                  <Line type="monotone" dataKey="b" name={periodLabel(b)} stroke="#002FA7" strokeWidth={2.5} dot={false} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className={`grid grid-cols-1 xl:grid-cols-2 gap-4 ${loading ? "opacity-60" : ""}`}>
            {GROUPS.map((g) => (
              <div key={g.name} className="border border-zinc-200 bg-white overflow-x-auto" data-testid={`mr-cmp-group-${g.name}`}>
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">{g.name}</th>
                      <th className="px-3 py-2 text-right font-semibold text-zinc-500">{periodLabel(a)}</th>
                      <th className="px-3 py-2 text-right font-semibold text-[#002FA7]">{periodLabel(b)}</th>
                      <th className="px-3 py-2 text-right font-semibold">Diferencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((it) => {
                      const va = data.a.metrics[it.key], vb = data.b.metrics[it.key];
                      const f = fmtV(it.type);
                      return (
                        <tr key={it.key} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`mr-cmp-row-${it.key}`}>
                          <td className="px-3 py-2 text-zinc-700">{it.label}</td>
                          <td className="px-3 py-2 text-right numeric text-zinc-500">{f(va)}</td>
                          <td className="px-3 py-2 text-right numeric font-semibold text-zinc-900">{f(vb)}</td>
                          <td className="px-3 py-2 text-right"><Delta a={va} b={vb} type={it.type} invert={it.invert} neutral={it.neutral} fmtV={f} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
